import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Host,
  Input,
  OnInit,
  Optional,
  Output,
  SkipSelf,
  ViewChild
} from '@angular/core';
import Inputmask from 'inputmask';
import { AbstractControl, ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'dc-input',
  templateUrl: './input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DCInputComponent),
      multi: true
    }
  ]
})
export class DCInputComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  @ViewChild('input', {static: false}) input;
  @ViewChild('inputSearch', {static: false}) inputSearch;

  @Input() value: any = null;
  @Input() autoFocus: boolean = false;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() requiredType: 'default' | 'full' = 'default' ;
  @Input() readOnly: boolean = false;
  @Input() max: number = 524288;
  @Input() min: number = null;
  @Input() autoComplete: string = 'off';
  @Input() type: 'text' | 'number' | 'email' | 'password' | 'textarea' | 'search' = 'text';
  @Input() rows: string;
  @Input() cols: string;
  @Input() label: string;
  @Input() placeholder: string = '';
  @Input() info: string = null;
  @Input() formControlName: string;
  @Input() errorMessages: any = {};
  @Input() showPassword: boolean = false;
  @Input() size: 'normal' | 'large' = null;
  @Input() platform: 'mobile' | 'website' = null;
  @Input() buttonText: string = null;
  @Input() resize: boolean = false;
  @Input() inputMask: boolean = false;
  @Input() inputMaskType: string | 'phone' = 'phone';

  @Output() searchTerm = new EventEmitter();
  @Output() emitValue = new EventEmitter();

  onChange: (fn: any) => void;
  onTouched: (fn: any) => void;

  errors: any[] = [];
  showErrors: boolean = false;
  hidePassword: boolean = true;

  constructor(
      @Optional() @Host() @SkipSelf() private controlContainer: ControlContainer,
  ) { }

  ngOnInit() {
    if (this.autoFocus !== false) { this.autoFocus = true; }
    if (this.required !== false) { this.required = true; }
    if (this.disabled !== false) { this.disabled = true; }
    if (this.readOnly !== false) { this.readOnly = true; }
    if (this.showPassword !== false && this.type === 'password') { this.showPassword = true; }
    if (!this.label) { this.label = ''; }
    this.checkRequired();
  }

  ngAfterViewInit() {
    if (this.autoFocus) {
      if (this.input) { this.input.nativeElement.focus(); }
      if (this.inputSearch) { this.inputSearch.nativeElement.focus(); }
    }
    if (this.inputMask) { this.setInputMask(); }
  }

  writeValue(value: any): void {
    this.value = value ? value : '';
    if (this.value === '') {
      this.errors = [];
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  checkRequired() {
    if (!this.formControlName) { return; }

    const formControl = this.controlContainer.control.get(this.formControlName);

    if (formControl.validator) {
      const validators = formControl.validator({}as AbstractControl);
      if (validators && validators.required) {
        this.required = true;
      }
    }
  }

  displayError() {
    if (!this.formControlName) { return; }

    const formControl = this.controlContainer.control.get(this.formControlName);

    if (this.inputMask && this.inputMaskType === 'phone' && this.value.includes('_')) { formControl.setErrors({phone_format: true}); }

    if (formControl.errors) {
      const errors = [];

      for (const [key, value] of Object.entries(formControl.errors)) {
        errors.push(key);
      }

      this.errors = errors;
    } else {
      this.errors = [];
    }
  }

  onChangeValue() {
    if (this.onChange) {
      this.onChange(this.value);
      this.displayError();
    }
  }

  toggleErrors() {
    this.showErrors = !this.showErrors;
  }

  togglePassword() {
    if (this.hidePassword) {
      this.hidePassword = false;
      this.type = 'text';
    } else {
      this.hidePassword = true;
      this.type = 'password';
    }
  }

  emitSearchTerm() {
    if (this.value) {
      this.searchTerm.emit({ value: this.value });
    }
  }

  valueEmit() {
    this.emitValue.emit({ value: this.value });
  }

  clearSearchTerm() {
    this.value = '';
    this.searchTerm.emit({ value: '' });
    if (this.onChange) { this.onChange(this.value); }
  }

  setInputMask() {
    let maskConfig: string;
    switch (this.inputMaskType) {
      case 'phone':
        maskConfig = '381 99 999999[9]';
        break;
      default:
        maskConfig = '381 99 999999[9]';
    }
    Inputmask({
      mask: maskConfig,
      clearIncomplete: true,
    }).mask(this.input.nativeElement);
  }
}
